import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { CurrencySymbolPosition, DEFAULT_CURRENCY_CONFIG, formatCurrency } from '../utils/format';

// 缓存过期时间（24小时，单位毫秒）
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000;
// 本地存储键名（函数，根据storeId生成不同的键）
const getCurrencySettingsKey = (storeId?: number) =>
  storeId ? `currencySettings_store_${storeId}` : 'currencySettings_global';
const getCurrencySettingsTimeKey = (storeId?: number) =>
  storeId ? `currencySettingsTime_store_${storeId}` : 'currencySettingsTime_global';

// 自定义国家接口
interface CustomCountry {
  code: string;
  symbol: string;
}

// 货币设置接口
export interface CurrencySettings {
  id: number;
  store_id: number | null;
  currency_symbol: string;
  symbol_position: CurrencySymbolPosition;
  decimal_places: number;
  preset_currency: string;
  customCountries: CustomCountry[];
  isGlobalSetting?: boolean;
}

// 上下文接口
interface CurrencyContextType {
  settings: CurrencySettings | null;
  loading: boolean;
  error: string | null;
  formatValue: (value: number | string | null | undefined) => string;
  getSymbolForCountry: (countryCode: string) => string;
  refresh: () => Promise<void>;
}

// 创建上下文
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// 提供器属性
interface CurrencyProviderProps {
  children: ReactNode;
  storeId?: number;
}

// 货币上下文提供器组件
export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children, storeId }) => {
  const [settings, setSettings] = useState<CurrencySettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const getDefaultSettings = (): CurrencySettings => ({
    id: 0,
    store_id: storeId || null,
    currency_symbol: '¥',
    symbol_position: CurrencySymbolPosition.PREFIX,
    decimal_places: 2,
    preset_currency: 'CNY',
    customCountries: []
  });

  // 获取货币设置
  const fetchSettings = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    // 首先尝试从localStorage获取缓存的设置（根据storeId使用不同的缓存键）
    try {
      const cacheKey = getCurrencySettingsKey(storeId);
      const cacheTimeKey = getCurrencySettingsTimeKey(storeId);
      const cachedSettings = localStorage.getItem(cacheKey);
      const cachedTimeStr = localStorage.getItem(cacheTimeKey);

      if (cachedSettings && cachedTimeStr) {
        const parsedSettings = JSON.parse(cachedSettings) as CurrencySettings;
        const cachedTime = parseInt(cachedTimeStr, 10);
        const now = Date.now();

        // 检查缓存是否有效且未过期
        if (!isNaN(cachedTime) && now - cachedTime < CACHE_EXPIRY_TIME) {
          setSettings(parsedSettings);
          setLoading(false);
          return; // 使用缓存数据，不进行API请求
        }
      }
    } catch (err) {
      // 缓存读取失败，继续从API获取
    }

    const token = localStorage.getItem('token');
    const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
    if (!token || isLoginPage) {
      // 未登录页面不请求受保护接口，直接使用默认配置
      setSettings(getDefaultSettings());
      setLoading(false);
      return;
    }

    try {
      const url = '/api/settings/currency';
      const params = storeId ? { storeId } : {};
      const response = await axios.get<CurrencySettings>(url, { params });
      setSettings(response.data);

      // 保存到localStorage（使用店铺特定的缓存键）
      const cacheKey = getCurrencySettingsKey(storeId);
      const cacheTimeKey = getCurrencySettingsTimeKey(storeId);
      localStorage.setItem(cacheKey, JSON.stringify(response.data));
      localStorage.setItem(cacheTimeKey, Date.now().toString());
    } catch (err: any) {
      if (err?.response?.status === 401) {
        // 登录前或会话失效时静默降级，避免登录页产生未授权噪音
        setSettings(getDefaultSettings());
        return;
      }

      console.error('[CurrencyContext] 获取货币设置失败:', err);
      setError('获取货币设置失败');

      // 如果API请求失败，尝试使用可能已过期的缓存
      try {
        const cacheKey = getCurrencySettingsKey(storeId);
        const cachedSettings = localStorage.getItem(cacheKey);
        if (cachedSettings) {
          setSettings(JSON.parse(cachedSettings));
        } else {
          // 没有缓存，使用默认值
          setSettings(getDefaultSettings());
        }
      } catch (cacheErr) {
        // 缓存读取也失败，使用默认值
        setSettings(getDefaultSettings());
      }
    } finally {
      setLoading(false);
    }
  };

  // 首次加载时获取数据
  useEffect(() => {
    fetchSettings();
  }, [storeId]);

  // 监听货币设置更新事件（从设置页面派发）
  useEffect(() => {
    const handleCurrencySettingsUpdated = (event: CustomEvent<{ storeId?: number }>) => {
      // 清除当前 storeId 对应的缓存
      const cacheKey = getCurrencySettingsKey(storeId);
      const cacheTimeKey = getCurrencySettingsTimeKey(storeId);
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);

      // 重新获取设置
      fetchSettings();
    };

    window.addEventListener('currencySettingsUpdated', handleCurrencySettingsUpdated as EventListener);

    return () => {
      window.removeEventListener('currencySettingsUpdated', handleCurrencySettingsUpdated as EventListener);
    };
  }, [storeId]);

  // 手动刷新并更新缓存
  const refresh = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // 清除现有缓存（使用店铺特定的缓存键）
      const cacheKey = getCurrencySettingsKey(storeId);
      const cacheTimeKey = getCurrencySettingsTimeKey(storeId);
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);

      // 重新获取设置
      const url = '/api/settings/currency';
      const params = storeId ? { storeId } : {};
      const response = await axios.get<CurrencySettings>(url, { params });

      setSettings(response.data);

      // 更新缓存
      localStorage.setItem(cacheKey, JSON.stringify(response.data));
      localStorage.setItem(cacheTimeKey, Date.now().toString());

      message.success('货币设置已刷新');
    } catch (err) {
      console.error('[CurrencyContext] 刷新货币设置失败:', err);
      setError('刷新货币设置失败');
      message.error('刷新货币设置失败');
    } finally {
      setLoading(false);
    }
  };

  // 格式化货币值
  const formatValue = (value: number | string | null | undefined): string => {
    if (!settings) {
      // 使用默认配置
      return formatCurrency(value);
    }

    // 判断是否为欧元，欧元符号应后置
    const isEuro = settings.currency_symbol === '€' || settings.preset_currency === 'EUR';
    const position = isEuro ? CurrencySymbolPosition.SUFFIX : settings.symbol_position;

    // 使用当前设置
    return formatCurrency(value, {
      symbol: settings.currency_symbol,
      position: position,
      decimalPlaces: settings.decimal_places
    });
  };

  // 根据国家代码获取货币符号
  const getSymbolForCountry = (countryCode: string): string => {
    if (!settings) {
      return DEFAULT_CURRENCY_CONFIG.symbol;
    }

    // 先检查自定义国家
    const customCountry = settings.customCountries.find(
      country => country.code.toLowerCase() === countryCode.toLowerCase()
    );

    if (customCountry) {
      return customCountry.symbol;
    }

    // 如果国家代码与预设货币相同，返回当前符号
    if (countryCode.toLowerCase() === settings.preset_currency.toLowerCase()) {
      return settings.currency_symbol;
    }

    // 默认返回当前符号
    return settings.currency_symbol;
  };

  const contextValue: CurrencyContextType = {
    settings,
    loading,
    error,
    formatValue,
    getSymbolForCountry,
    refresh
  };

  return <CurrencyContext.Provider value={contextValue}>{children}</CurrencyContext.Provider>;
};

// 使用货币上下文的Hook
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);

  if (context === undefined) {
    throw new Error('useCurrency必须在CurrencyProvider内部使用');
  }

  return context;
};

export default CurrencyContext;
